import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import {
  ActionIcon,
  Box,
  Button,
  Group,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react'
import Client from '../../models/client'
import {
  createShopsOption,
  deleteShopsOption,
  fetchShopsOptions,
  updateShopsOption,
} from '../api'
import { BaseModal, requireLabelStyles, requiredMark } from './BaseModal'
import { confirmDelete, confirmDiscardChanges, confirmDiscardEdit } from '../utils/confirm'

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
}

export interface ShopsOption {
  id: number
  client_id: number
  option_key: string
  option_name: string
  option_values: string[]
}

interface FormValues {
  option_key: string
  option_name: string
  option_values: string[]
}

const OPTION_KEY_DATA = [
  { value: 'select_option1', label: '選択肢1' },
  { value: 'select_option2', label: '選択肢2' },
  { value: 'select_option3', label: '選択肢3' },
  { value: 'select_option4', label: '選択肢4' },
  { value: 'select_option5', label: '選択肢5' },
]

const OPTION_KEY_LABEL: Record<string, string> = {
  select_option1: '選択肢1',
  select_option2: '選択肢2',
  select_option3: '選択肢3',
  select_option4: '選択肢4',
  select_option5: '選択肢5',
}

type ViewMode = 'list' | 'edit'

export const ShopsOptionModal: React.FC<Props> = ({ opened, onClose, client }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [editingOption, setEditingOption] = useState<ShopsOption | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: shopsOptions, mutate: mutateShopsOptions } = useSWR<ShopsOption[]>(
    opened ? `/admin/clients/${client.id}/shop_options` : null,
    () => fetchShopsOptions(client.id),
  )

  const form = useForm<FormValues>({
    initialValues: {
      option_key: '',
      option_name: '',
      option_values: [''],
    },
    validate: {
      option_key: (v) => (!v ? '必須です' : null),
      option_name: (v) => (!v ? '必須です' : null),
    },
  })

  useEffect(() => {
    if (!opened) {
      setViewMode('list')
      setEditingOption(null)
      form.reset()
    }
  }, [opened])

  const handleClickCreate = () => {
    setEditingOption(null)
    form.setValues({
      option_key: '',
      option_name: '',
      option_values: [''],
    })
    form.resetDirty()
    setViewMode('edit')
  }

  const handleClickEdit = (option: ShopsOption) => {
    setEditingOption(option)
    form.setValues({
      option_key: option.option_key,
      option_name: option.option_name,
      option_values: option.option_values?.length ? option.option_values : [''],
    })
    form.resetDirty()
    setViewMode('edit')
  }

  const handleClickDelete = async (option: ShopsOption) => {
    if (!confirmDelete(`「${option.option_name}」`)) return
    try {
      await deleteShopsOption(client.id, option.option_key)
      await mutateShopsOptions()
      notifications.show({ message: '削除しました', color: 'teal' })
    } catch (e) {
      console.error('選択肢の削除に失敗しました', e)
      notifications.show({ message: '削除に失敗しました', color: 'red' })
    }
  }

  const handleAddValue = () => {
    form.setFieldValue('option_values', [...form.values.option_values, ''])
  }

  const handleRemoveValue = (index: number) => {
    if (!confirmDelete('選択肢の値')) return
    const next = form.values.option_values.filter((_, i) => i !== index)
    form.setFieldValue('option_values', next.length > 0 ? next : [''])
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const trimmedValues = values.option_values.map((v) => v.trim()).filter((v) => v !== '')
      const payload = {
        option_key: values.option_key,
        option_name: values.option_name,
        option_values: trimmedValues,
      }
      if (editingOption) {
        await updateShopsOption(client.id, values.option_key, payload)
      } else {
        await createShopsOption(client.id, payload)
      }
      await mutateShopsOptions()
      notifications.show({ message: '保存しました', color: 'teal' })
      setViewMode('list')
      setEditingOption(null)
    } catch (e) {
      console.error('選択肢の保存に失敗しました', e)
      notifications.show({ message: '保存に失敗しました', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelEdit = () => {
    if (form.isDirty() && !confirmDiscardEdit()) return
    setViewMode('list')
    setEditingOption(null)
    form.reset()
  }

  const handleModalClose = () => {
    if (viewMode === 'edit' && form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const footerStyle: React.CSSProperties = {
    borderTop: '1px solid #0000001A',
    backgroundColor: '#fff',
    flexShrink: 0,
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleModalClose}
      title={
        viewMode === 'list'
          ? '店舗選択肢設定'
          : editingOption
            ? '選択肢設定を編集'
            : '新しい選択肢設定を追加'
      }
    >
      {viewMode === 'list' && (
        <>
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg='#00000008'>
            <Stack gap='md' p='md'>
              <Text size='sm' c='dimmed'>店舗の選択肢項目を設定できます。</Text>

              <Table withTableBorder withColumnBorders highlightOnHover bg='#fff'>
                <Table.Thead bg='#00000008'>
                  <Table.Tr>
                    <Table.Th>選択肢キー</Table.Th>
                    <Table.Th>選択肢名</Table.Th>
                    <Table.Th>値の数</Table.Th>
                    <Table.Th>値</Table.Th>
                    <Table.Th w={80} ta="center">編集</Table.Th>
                    <Table.Th w={80} ta="center">削除</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shopsOptions && shopsOptions.length > 0 ? (
                    shopsOptions.map((option) => (
                      <Table.Tr key={option.option_key}>
                        <Table.Td>{OPTION_KEY_LABEL[option.option_key] ?? option.option_key}</Table.Td>
                        <Table.Td>{option.option_name}</Table.Td>
                        <Table.Td>{option.option_values?.length ?? 0}</Table.Td>
                        <Table.Td>{option.option_values?.join(', ') ?? ''}</Table.Td>
                        <Table.Td ta="center">
                          <Tooltip label='編集'>
                            <ActionIcon
                              variant='subtle'
                              color='gray'
                              onClick={() => handleClickEdit(option)}
                            >
                              <PencilSimpleIcon size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td ta="center">
                          <Tooltip label='削除'>
                            <ActionIcon
                              variant='subtle'
                              color='red'
                              onClick={() => handleClickDelete(option)}
                            >
                              <TrashIcon size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={6} align='center'>
                        <Text c='dimmed' py='md'>選択肢設定はありません</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Stack>
          </ScrollArea>

          <Box p='md' style={footerStyle}>
            <Group justify='flex-end'>
              <Button
                variant='filled'
                radius='xl'
                onClick={handleClickCreate}
                leftSection={<PlusIcon size={16} weight='bold' />}
              >
                新しい選択肢を追加
              </Button>
            </Group>
          </Box>
        </>
      )}

      {viewMode === 'edit' && (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
              e.preventDefault()
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg='#00000008'>
            <Stack gap='md' p='md'>
              <Select
                label={<><span>選択肢キー</span>{requiredMark}</>}
                data={OPTION_KEY_DATA}
                {...form.getInputProps('option_key')}
                disabled={!!editingOption}
                allowDeselect={false}
                styles={requireLabelStyles}
              />

              <TextInput
                label={<><span>選択肢名</span>{requiredMark}</>}
                {...form.getInputProps('option_name')}
                styles={requireLabelStyles}
              />

              <Stack gap='xs'>
                <Text size='sm' fw={500}>選択肢の値</Text>
                {form.values.option_values.map((_, index) => (
                  <Group key={index} gap='xs' align='flex-end'>
                    <TextInput
                      label={`値 ${index + 1}`}
                      {...form.getInputProps(`option_values.${index}`)}
                      style={{ flex: 1 }}
                    />
                    <ActionIcon
                      variant='subtle'
                      color='red'
                      onClick={() => handleRemoveValue(index)}
                      size='lg'
                      mb={4}
                    >
                      <TrashIcon size={18} />
                    </ActionIcon>
                  </Group>
                ))}
                <Button
                  variant='outline'
                  radius='xl'
                  onClick={handleAddValue}
                  leftSection={<PlusIcon size={16} weight='bold' />}
                  w='fit-content'
                >
                  値を追加
                </Button>
              </Stack>
            </Stack>
          </ScrollArea>

          <Box p='md' style={footerStyle}>
            <Group justify='flex-end' gap={8}>
              <Button
                variant='outline'
                radius='xl'
                color='gray'
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                キャンセル
              </Button>
              <Button
                variant='filled'
                radius='xl'
                type='submit'
                loading={submitting}
              >
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}
